import { skipToken } from "@reduxjs/toolkit/query";
import {
  copilotProposalIdSchema,
  type CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import {
  buildGradingOutcome,
  getOutcomeValueAndLabel,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  PlaybookBranch,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  PlaybookTestResultStatus,
  type AdminApiPlaybookTestResult,
  type AdminApiPlaybookTestSpec,
  type AdminApiPlaybookTestSpecResult,
} from "@repo/app-types/entities/teammateAgent";
import type { TeammateAgentTestCaseProposalData } from "@repo/app-types/entities/teammateAgentProposal";
import { checkPlaybookTestOutcomeMatch } from "@repo/app-types/helpers/playbookTestVerdict";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconCheck,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { useEffect, useMemo, useState, type FC } from "react";
import { useParams, useSearchParams } from "react-router";
import { useResolveProposalMutation } from "../../api/entities/copilotProposal.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  useAllPlaybookTestSpecsQuery,
  useCreatePlaybookTestSpecMutation,
  useDeletePlaybookTestSpecMutation,
  useUpdatePlaybookTestSpecMutation,
} from "../../api/entities/playbookTestSpec.js";
import {
  useCreateTeammateAgentTestRunsMutation,
  useGetTeammateAgentQuery,
  useGetTeammateAgentTestSpecsResultsQuery,
} from "../../api/entities/teammateAgent.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { CopilotProposalBanner } from "../../components/copilotProposalBanner.js";
import { PageBody } from "../../components/layout/pageLayout.js";
import { readPlaybookTestResultStatus } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { useWaitForAgentPrompt } from "../../hooks/useWaitForAgentPrompt.js";
import {
  COPILOT_PROPOSAL_QUERY_PARAM,
  useDeepLinkedProposal,
} from "../copilot/copilotDeepLinks.js";
import { useReportEditorProposal } from "../copilot/editorProposalsSlice.js";
import {
  PlaybookTestSpecEditorSlideover,
  type PlaybookTestSpecDraft,
  type PlaybookTestSpecProposal,
} from "./playbookTestSpecEditorSlideover.js";
import { describePlaybookTestVerdictTallies } from "./playbookTestVerdictSummary.js";
import {
  checkIsTestCaseProposalData,
  TEST_SPEC_QUERY_PARAM,
} from "./teammateAgentProposalCardContent.js";

/**
 * The test case an Ask Alpharun proposal was just applied to, from the chat
 * card's `?proposalId=` and `?testSpecId=` params or from Apply in the
 * slideover. It is highlighted, and reported to the chat as viewed, until the
 * user edits or adds a case.
 */
interface ViewedTestCase {
  proposalId: CopilotProposalId;
  testSpecId: string;
}

interface LinkedProposal {
  id: CopilotProposalId;
  teammateAgentId: TeammateId;
  data: TeammateAgentTestCaseProposalData;
}

/** What a proposal fills the slideover's form with. */
type ProposedTestSpecForm = Pick<
  PlaybookTestSpecProposal,
  "summary" | "name" | "description" | "expected"
>;

interface CreateTestCaseProposal extends ProposedTestSpecForm {
  kind: "create_test_case";
}

interface UpdateTestCaseProposal extends ProposedTestSpecForm {
  kind: "update_test_case";
  testSpec: AdminApiPlaybookTestSpec;
}

interface DeleteTestCaseProposal {
  kind: "delete_test_case";
  testSpecId: string;
}

type LinkedTestCaseProposal =
  | CreateTestCaseProposal
  | UpdateTestCaseProposal
  | DeleteTestCaseProposal;

/** Null when the case an update or delete targets is gone. */
function readLinkedTestCaseProposal(
  data: TeammateAgentTestCaseProposalData,
  testSpecs: readonly AdminApiPlaybookTestSpec[],
  outcomeDefinition: OutcomeDefinition | undefined,
): LinkedTestCaseProposal | null {
  const testSpec = testSpecs.find((spec) => spec.id === data.test_spec_id);
  switch (data.kind) {
    case "create_test_case":
      return {
        kind: "create_test_case",
        summary: data.summary,
        name: data.name,
        description: data.description,
        expected: {
          nodes: {},
          outcomes:
            outcomeDefinition && data.expected_outcome
              ? buildGradingOutcome(outcomeDefinition, data.expected_outcome)
              : undefined,
        },
      };
    case "update_test_case":
      if (!testSpec) {
        return null;
      }
      return {
        kind: "update_test_case",
        testSpec,
        summary: data.summary,
        name: data.proposed.name,
        description: data.proposed.description,
        expected: {
          ...testSpec.expected,
          outcomes:
            outcomeDefinition && data.proposed.expected_outcome
              ? buildGradingOutcome(
                  outcomeDefinition,
                  data.proposed.expected_outcome,
                )
              : undefined,
        },
      };
    case "delete_test_case":
      return testSpec
        ? { kind: "delete_test_case", testSpecId: testSpec.id }
        : null;
  }
}

function notifyProposalUnavailable() {
  showToast({
    variant: ToastVariant.Error,
    title: "This suggestion is no longer available.",
  });
}

export const TeammateAgentTestingTab: FC = () => {
  const { teammateAgentId } = useParams();
  if (!teammateAgentId) {
    throw new Error("Missing params.");
  }

  const modalNavigation = useModalNavigation();

  const { data: teammateAgent, isLoading: isLoadingTeammateAgent } =
    useGetTeammateAgentQuery(teammateAgentId);

  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;

  // The draft playbook: test runs grade against it, and a proposal's expected
  // outcome is built from its outcome definition.
  const { data: playbook } = usePlaybookByIdQuery(
    playbookId ? { id: playbookId, branch: PlaybookBranch.Draft } : skipToken,
  );

  // Fetch all test specs for the playbook
  const { data: allTestSpecs, isLoading: isLoadingTestSpecs } =
    useAllPlaybookTestSpecsQuery(playbookId ? { playbookId } : skipToken);

  // Track if there are in-progress tests to enable polling
  const [hasInProgressTests, setHasInProgressTests] = useState(false);

  // Fetch test results for specs that have been run
  const {
    data: testSpecsResults,
    isLoading: isLoadingTestSpecsResults,
    refetch: refetchTestSpecsResults,
  } = useGetTeammateAgentTestSpecsResultsQuery(
    playbookId ? { teammateAgentId, playbookId } : skipToken,
    { pollingInterval: hasInProgressTests ? 5000 : 0 },
  );

  const [createPlaybookTestSpec, { isLoading: isCreatingTestSpec }] =
    useCreatePlaybookTestSpecMutation();
  const [updatePlaybookTestSpec, { isLoading: isUpdatingTestSpec }] =
    useUpdatePlaybookTestSpecMutation();
  const [deletePlaybookTestSpec, { isLoading: isDeletingTestSpec }] =
    useDeletePlaybookTestSpecMutation();
  const [createTeammateAgentTestRuns] =
    useCreateTeammateAgentTestRunsMutation();

  const { waitForPromptReady } = useWaitForAgentPrompt({ teammateAgentId });

  const [searchParams, setSearchParams] = useSearchParams();
  const [viewedTestCase, setViewedTestCase] = useState<ViewedTestCase | null>(
    () => {
      const proposalId = copilotProposalIdSchema.safeParse(
        searchParams.get(COPILOT_PROPOSAL_QUERY_PARAM),
      ).data;
      const testSpecId = searchParams.get(TEST_SPEC_QUERY_PARAM);
      return proposalId && testSpecId ? { proposalId, testSpecId } : null;
    },
  );
  useEffect(() => {
    if (searchParams.has(TEST_SPEC_QUERY_PARAM)) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(COPILOT_PROPOSAL_QUERY_PARAM);
          next.delete(TEST_SPEC_QUERY_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [editingTestSpec, setEditingTestSpec] =
    useState<AdminApiPlaybookTestSpec | null>(null);
  const [testSpecToDelete, setTestSpecToDelete] = useState<string | null>(null);
  const [runningTestIds, setRunningTestIds] = useState<Set<string>>(new Set());
  const [isRunningAllTests, setIsRunningAllTests] = useState(false);

  // The proposal is stored as loaded and what it shows is derived, so it hides
  // when its case is gone or the tab now shows another agent.
  const [linkedProposal, setLinkedProposal] = useState<LinkedProposal | null>(
    null,
  );
  const linkedTestCase =
    linkedProposal?.teammateAgentId === teammateAgentId &&
    allTestSpecs &&
    playbook
      ? readLinkedTestCaseProposal(
          linkedProposal.data,
          allTestSpecs,
          getOutcomeDefinitionFromPlaybook(playbook),
        )
      : null;
  const proposalToEdit =
    linkedTestCase?.kind === "delete_test_case" ? null : linkedTestCase;
  const proposalToDelete =
    linkedTestCase?.kind === "delete_test_case" ? linkedTestCase : null;
  const isEditorOpen = isSlideoverOpen || proposalToEdit !== null;
  const testSpecToEdit =
    proposalToEdit?.kind === "update_test_case"
      ? proposalToEdit.testSpec
      : proposalToEdit
        ? null
        : editingTestSpec;

  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const isApplyingProposal =
    isCreatingTestSpec ||
    isUpdatingTestSpec ||
    isDeletingTestSpec ||
    isResolvingProposal;

  // A hidden proposal isn't reported, so the chat card keeps its own action.
  const reportedLinkedProposalId =
    linkedTestCase || isApplyingProposal ? linkedProposal?.id : undefined;
  useReportEditorProposal(
    reportedLinkedProposalId ?? viewedTestCase?.proposalId,
    reportedLinkedProposalId !== undefined && isApplyingProposal,
  );

  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("teammateAgent", {
      onUnavailable: notifyProposalUnavailable,
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    const { id, resource_id, data } = deepLinkProposal;
    if (checkIsTestCaseProposalData(data)) {
      setLinkedProposal({ id, teammateAgentId: resource_id, data });
    }
  }, [deepLinkProposal, clearDeepLinkProposalId]);

  const closeSlideover = () => {
    setIsSlideoverOpen(false);
    setEditingTestSpec(null);
    if (proposalToEdit) {
      setLinkedProposal(null);
    }
  };

  // The write stands, so an applied proposal clears even if recording it
  // fails. A discarded one stays unless it has moved on (409).
  const resolveLinkedProposal = async (status: "applied" | "discarded") => {
    if (!linkedProposal) {
      return;
    }
    try {
      await resolveProposal({ proposalId: linkedProposal.id, status }).unwrap();
    } catch (err) {
      if (isConflictQueryError(err)) {
        notifyProposalUnavailable();
      } else if (status === "applied") {
        console.error("Failed to mark the suggestion as applied:", err);
        showToast({
          variant: ToastVariant.Error,
          title:
            "The change was saved, but the suggestion could not be marked as applied.",
        });
      } else {
        console.error("Failed to discard the suggestion:", err);
        showToast({
          variant: ToastVariant.Error,
          title: "Failed to discard the suggestion",
          description: "Please try again.",
        });
        return;
      }
    }
    // A proposal takes over the slideover, so resolving one closes it fully.
    if (proposalToEdit) {
      closeSlideover();
    } else {
      setLinkedProposal(null);
    }
  };

  /** Writes the case and returns its id, or null when the write failed. */
  const saveTestSpec = async (
    draft: PlaybookTestSpecDraft,
    testSpecId?: string,
    proposalId?: CopilotProposalId,
  ): Promise<string | null> => {
    if (!playbookId) {
      return null;
    }
    try {
      if (testSpecId) {
        await updatePlaybookTestSpec({
          playbookId,
          testSpecId,
          payload: draft,
        }).unwrap();
        return testSpecId;
      }
      // A case created from a proposal takes the id the proposal minted.
      const createdTestSpec = await createPlaybookTestSpec({
        playbookId,
        payload: proposalId ? { ...draft, proposal_id: proposalId } : draft,
      }).unwrap();
      return createdTestSpec.id;
    } catch (error) {
      console.error("Failed to save test spec:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to save test case",
        description: "Please try again.",
      });
      return null;
    }
  };

  // Waits for any pending prompt regeneration, so the run tests the saved
  // instructions.
  const runTestSpec = async (testSpecId: string) => {
    try {
      const promptReady = await waitForPromptReady();
      if (!promptReady) {
        showToast({
          variant: ToastVariant.Error,
          title: "Agent prompt still generating",
          description:
            "Please wait a moment for the agent to finish updating and try again.",
        });
        return;
      }
      await createTeammateAgentTestRuns({
        teammateAgentId,
        playbookTestSpecIds: [testSpecId],
      }).unwrap();
    } catch (error) {
      console.error("Failed to trigger test run:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to start test",
        description: "Please try again.",
      });
    }
  };

  const handleSubmitTestSpec = async (
    draft: PlaybookTestSpecDraft,
    testSpecId?: string,
  ) => {
    const savedTestSpecId = await saveTestSpec(draft, testSpecId);
    if (savedTestSpecId) {
      await runTestSpec(savedTestSpecId);
    }
  };

  const applyLinkedProposal = async (
    draft: PlaybookTestSpecDraft,
    testSpecId?: string,
  ) => {
    if (!linkedProposal) {
      return;
    }
    const savedTestSpecId = await saveTestSpec(
      draft,
      testSpecId,
      linkedProposal.id,
    );
    if (!savedTestSpecId) {
      return;
    }
    await resolveLinkedProposal("applied");
    setViewedTestCase({
      proposalId: linkedProposal.id,
      testSpecId: savedTestSpecId,
    });
    await runTestSpec(savedTestSpecId);
  };

  const removeLinkedTestCase = async (testSpecId: string) => {
    if (!playbookId) {
      return;
    }
    try {
      await deletePlaybookTestSpec({ playbookId, testSpecId }).unwrap();
    } catch (error) {
      console.error("Failed to delete test spec:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to delete test case",
        description: "Please try again.",
      });
      return;
    }
    await resolveLinkedProposal("applied");
  };

  // Merge all test specs with their results (if they have been run)
  const mergedTestSpecsResults = useMemo<
    AdminApiPlaybookTestSpecResult[]
  >(() => {
    if (!allTestSpecs) {
      return [];
    }

    // Create a map of test spec ID to result for quick lookup
    const resultsMap = new Map<string, AdminApiPlaybookTestSpecResult>();
    testSpecsResults?.forEach((result) => {
      resultsMap.set(result.playbook_test_spec.id, result);
    });

    // Transform all test specs into AdminApiPlaybookTestSpecResult format
    return allTestSpecs.map((testSpec) => {
      // If we have a result for this test spec, use it
      const existingResult = resultsMap.get(testSpec.id);
      if (existingResult) {
        return existingResult;
      }

      // Otherwise, create a placeholder result structure
      return {
        id: testSpec.id,
        playbook_test_spec: testSpec,
      };
    });
  }, [allTestSpecs, testSpecsResults]);

  const sortedTestSpecsResults = mergedTestSpecsResults.toSorted((a, b) =>
    a.playbook_test_spec.name.localeCompare(b.playbook_test_spec.name),
  );

  // Sync hasInProgressTests state to enable/disable RTK Query polling
  useEffect(() => {
    const hasInProgress = sortedTestSpecsResults.some(
      (testCase) =>
        testCase.playbook_test_result?.status ===
        PlaybookTestResultStatus.IN_PROGRESS,
    );
    setHasInProgressTests(hasInProgress);
  }, [sortedTestSpecsResults]);

  const columns = useMemo<
    EntityTableColumnDefinition<AdminApiPlaybookTestSpecResult>[]
  >(() => {
    const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

    // Whether the run's outcome is the one the case expects, with a tooltip
    // showing both. It says nothing about the rest of the verdict: an assertion
    // that failed is the status pill's and the tallies' to report.
    const renderOutcomeIndicator = (
      spec: AdminApiPlaybookTestSpec,
      result: AdminApiPlaybookTestResult,
    ) => {
      if (!outcomeDefinition || !result.output) {
        return null;
      }

      const { value: expectedValue, label: expectedLabel } =
        getOutcomeValueAndLabel(spec.expected.outcomes, outcomeDefinition);
      const { label: actualLabel } = getOutcomeValueAndLabel(
        result.output.outcomes,
        outcomeDefinition,
      );

      // No expected outcome = nothing to show
      if (expectedValue === undefined) {
        return null;
      }

      const outcomeMatches = checkPlaybookTestOutcomeMatch(
        spec.expected.outcomes,
        result.output.outcomes,
        outcomeDefinition,
      );

      return (
        <TooltipTrigger>
          <TooltipHoverTarget shape="rounded">
            {outcomeMatches ? (
              <IconCheck className="size-5 text-success" />
            ) : (
              <IconX className="size-5 text-destructive" />
            )}
          </TooltipHoverTarget>
          <SimpleTooltip>
            Expected outcome: {expectedLabel}
            <br />
            Result outcome: {actualLabel}
          </SimpleTooltip>
        </TooltipTrigger>
      );
    };

    // The tallies the stored verdict carries, for a run that was judged.
    const renderVerdictTallies = (result: AdminApiPlaybookTestResult) => {
      const verdict = result.output?.verdict;
      if (!verdict) {
        return null;
      }

      const tallies = describePlaybookTestVerdictTallies(
        verdict,
        result.output?.assertion_verdicts ?? [],
      );
      if (!tallies) {
        return null;
      }

      return <div className="text-xs text-muted-foreground">{tallies}</div>;
    };

    return [
      {
        id: "test-case",
        header: "Test case",
        isRowHeader: true,
        renderCell: (testCase) => (
          <div className="group relative flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {testCase.playbook_test_spec.name}
              </span>
              <span className="mt-1 text-xs text-muted-foreground">
                {testCase.playbook_test_spec.description}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Test result",
        renderCell: (testCase) => {
          const { playbook_test_spec: spec, playbook_test_run: run } = testCase;

          // Use the same helper as testStatusPill to determine status
          const status = readPlaybookTestResultStatus(
            spec,
            testCase.playbook_test_result,
            outcomeDefinition,
          );

          switch (status) {
            case "not_run":
              return (
                <span className="mt-1 text-xs text-muted-foreground">
                  Test case not executed yet
                </span>
              );
            case "in_progress":
              return (
                <div className="flex items-center gap-2">
                  <div className="-mx-1">
                    <TooltipTrigger>
                      <TooltipHoverTarget shape="rounded">
                        <Loader style={LoaderStyle.SPINNER} />
                      </TooltipHoverTarget>
                      <SimpleTooltip>{`Started on ${run ? format(new Date(run.created_at), "PP p") : "unknown"}`}</SimpleTooltip>
                    </TooltipTrigger>
                  </div>
                  <div className="text-xs whitespace-nowrap text-muted-foreground">
                    Running test...
                  </div>
                </div>
              );
            case "failing": {
              const result = testCase.playbook_test_result;
              // Check if it's an error vs outcome mismatch
              if (result?.status === PlaybookTestResultStatus.ERROR) {
                return (
                  <TooltipTrigger>
                    <TooltipHoverTarget className="mt-1 text-xs text-muted-foreground">
                      An error occurred
                    </TooltipHoverTarget>
                    <SimpleTooltip>{`Ran on ${run ? format(new Date(run.created_at), "PP p") : "unknown"}`}</SimpleTooltip>
                  </TooltipTrigger>
                );
              }
              // The verdict says it failed - show X indicator
              return (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {result && renderOutcomeIndicator(spec, result)}
                    <div className="text-xs whitespace-nowrap text-muted-foreground">
                      {run && format(new Date(run.created_at), "PP p")}
                    </div>
                  </div>
                  {result && renderVerdictTallies(result)}
                </div>
              );
            }
            case "passing": {
              const result = testCase.playbook_test_result;
              return (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {result && renderOutcomeIndicator(spec, result)}
                    <div className="text-xs whitespace-nowrap text-muted-foreground">
                      {run && format(new Date(run.created_at), "PP p")}
                    </div>
                  </div>
                  {result && renderVerdictTallies(result)}
                </div>
              );
            }
          }
        },
      },
      {
        id: "actions",
        header: "",
        renderCell: (testCase) => (
          <div className="flex items-center gap-2">
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconRefresh className="h-4 w-4" />}
              isDisabled={runningTestIds.has(testCase.playbook_test_spec.id)}
              onClick={async () => {
                const testSpecId = testCase.playbook_test_spec.id;
                setRunningTestIds((prev) => new Set(prev).add(testSpecId));
                try {
                  // Wait for any pending prompt regeneration to complete
                  const promptReady = await waitForPromptReady();
                  if (!promptReady) {
                    showToast({
                      variant: ToastVariant.Error,
                      title: "Agent prompt still generating",
                      description:
                        "Please wait a moment for the agent to finish updating and try again.",
                    });
                    return;
                  }

                  await createTeammateAgentTestRuns({
                    teammateAgentId,
                    playbookTestSpecIds: [testSpecId],
                  }).unwrap();
                  showToast({
                    variant: ToastVariant.Success,
                    title: "Test started",
                    description: `This can take a few minutes to complete.`,
                  });
                  // Refetch to show the in_progress status
                  refetchTestSpecsResults();
                } catch (error) {
                  showToast({
                    variant: ToastVariant.Error,
                    title: "Failed to start test",
                    description: "Please try again.",
                  });
                } finally {
                  setRunningTestIds((prev) => {
                    const next = new Set(prev);
                    next.delete(testSpecId);
                    return next;
                  });
                }
              }}
            />
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconPencil className="h-4 w-4" />}
              onClick={() => {
                setViewedTestCase(null);
                setEditingTestSpec(testCase.playbook_test_spec);
                setIsSlideoverOpen(true);
              }}
            />
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconTrash className="h-4 w-4" />}
              onClick={() => {
                setTestSpecToDelete(testCase.playbook_test_spec.id);
              }}
            />
          </div>
        ),
      },
    ];
  }, [
    teammateAgentId,
    createTeammateAgentTestRuns,
    playbook,
    refetchTestSpecsResults,
    runningTestIds,
    waitForPromptReady,
  ]);

  return (
    <PageBody width={PageWidth.Wide}>
      <div className="flex flex-wrap justify-between gap-4">
        {/* <div className="rounded-lg border border-border-light bg-white px-4 py-2">
          <FeedbackAccuracySummary
            accuracyData={{
              total: 5,
              correct: 3,
              incorrect: 1,
              close: 1,
              accuracy: 0,
            }}
          />
        </div> */}
        <div className="flex flex-row gap-2">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={async () => {
              if (!allTestSpecs || allTestSpecs.length === 0) {
                return;
              }
              setIsRunningAllTests(true);
              try {
                // Wait for any pending prompt regeneration to complete
                const promptReady = await waitForPromptReady();
                if (!promptReady) {
                  showToast({
                    variant: ToastVariant.Error,
                    title: "Agent prompt still generating",
                    description:
                      "Please wait a moment for the agent to finish updating and try again.",
                  });
                  return;
                }

                const allTestSpecIds = allTestSpecs.map((spec) => spec.id);
                await createTeammateAgentTestRuns({
                  teammateAgentId,
                  playbookTestSpecIds: allTestSpecIds,
                }).unwrap();
                showToast({
                  variant: ToastVariant.Success,
                  title: "All tests started",
                  description: `This can take a few minutes to complete.`,
                });
                // Refetch to show the in_progress status
                refetchTestSpecsResults();
              } catch (error) {
                showToast({
                  variant: ToastVariant.Error,
                  title: "Failed to start tests",
                  description: "Please try again.",
                });
              } finally {
                setIsRunningAllTests(false);
              }
            }}
            icon={<IconPlayerPlay className="h-4 w-4" />}
            isDisabled={
              !allTestSpecs || allTestSpecs.length === 0 || isRunningAllTests
            }
            isLoading={isRunningAllTests}
          >
            Run all tests
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              setViewedTestCase(null);
              setEditingTestSpec(null);
              setIsSlideoverOpen(true);
            }}
            icon={<IconPlus className="h-4 w-4" />}
          >
            Add test case
          </Button>
        </div>
      </div>

      {proposalToDelete ? (
        <CopilotProposalBanner
          message="Ask Alpharun suggests removing this test case."
          isDestructive
          discardLabel="Keep it"
          confirmLabel="Remove test case"
          onDiscard={() => {
            void resolveLinkedProposal("discarded");
          }}
          onConfirm={() => {
            void removeLinkedTestCase(proposalToDelete.testSpecId);
          }}
          isDisabled={isApplyingProposal}
        />
      ) : null}

      <div className="flex flex-col gap-4 md:flex-row">
        <EntityTable
          aria-label="Agent test cases"
          columns={columns}
          entities={sortedTestSpecsResults}
          isLoading={
            isLoadingTeammateAgent ||
            isLoadingTestSpecs ||
            isLoadingTestSpecsResults
          }
          emptyMessage="No test cases have been created yet."
          getRowClassName={(testCase) => {
            const testSpecId = testCase.playbook_test_spec.id;
            if (proposalToDelete?.testSpecId === testSpecId) {
              return "bg-rose-50";
            }
            if (viewedTestCase?.testSpecId === testSpecId) {
              return "bg-indigo-50/60";
            }
            return undefined;
          }}
          onEntityAction={(testCase) => {
            const testSpecId = testCase.playbook_test_spec.id;
            const testRunId = testCase.playbook_test_run?.id;
            const testResultId = testCase.playbook_test_result?.id;
            if (testResultId && testRunId) {
              modalNavigation.push(
                `specs/${encodeURIComponent(testSpecId)}/runs/${encodeURIComponent(testRunId)}/results/${encodeURIComponent(testResultId)}/assessment`,
              );
            }
          }}
        />
      </div>

      <NavigationSlideoverContainer />

      <SlideoverContainer
        size={Size.SMALL}
        isDismissable
        isOpen={isEditorOpen}
        onOpenChange={(newIsOpen) => {
          if (!newIsOpen) {
            closeSlideover();
          }
        }}
      >
        {playbookId && isEditorOpen ? (
          <PlaybookTestSpecEditorSlideover
            // A proposal always opens a fresh form, even over an open editor.
            key={proposalToEdit ? "proposal" : (testSpecToEdit?.id ?? "new")}
            playbookId={playbookId}
            existingTestSpec={testSpecToEdit ?? undefined}
            proposal={
              proposalToEdit
                ? {
                    ...proposalToEdit,
                    onApply: (draft, testSpecId) => {
                      void applyLinkedProposal(draft, testSpecId);
                    },
                    onDiscard: () => {
                      void resolveLinkedProposal("discarded");
                    },
                    isResolving: isApplyingProposal,
                  }
                : undefined
            }
            onClose={closeSlideover}
            onSubmit={handleSubmitTestSpec}
          />
        ) : null}
      </SlideoverContainer>
      <SimpleModal
        isOpen={Boolean(testSpecToDelete)}
        onCancel={() => {
          setTestSpecToDelete(null);
        }}
        variant={SimpleModalVariant.Warning}
        title="Delete test case"
        subtitle="Are you sure you want to delete this test case?"
        confirmButtonText="Delete test case"
        onConfirm={() => {
          if (testSpecToDelete && playbookId) {
            deletePlaybookTestSpec({
              playbookId,
              testSpecId: testSpecToDelete,
            });
          }
          setTestSpecToDelete(null);
        }}
      />
    </PageBody>
  );
};
